"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.analyticsEvents = void 0;
const utils_1 = require("./utils");
function capitalizeFirstLetter(string = '') {
    if (!string)
        return ''; // avoids errors from string being null
    return string.charAt(0).toUpperCase() + string.slice(1);
}
// There is no DataLayer in Bit workspace so Analytics events to be tested in consuming app
/**
 * format variant data for analytics events
 * @param {object} item Cart item object
 */
function getVariant(item = {}) {
    const attributes = item.attributes || item.productAttributes;
    if (!attributes) {
        return '';
    }
    return (attributes
        .map((attribute) => {
        /** The 'Standard' part of the Queen size pillows is removed in service-cart-checkout.
        * Here it is added back to maintain consistency with the rest of the pages.
        */
        if ((0, utils_1.isPillowSku)(item.productCode) && attribute.value.toLowerCase() === 'queen') {
            return 'Standard/Queen';
        }
        return attribute.value.replace('"', ''); // Remove quotes from the value
    })
        .join('/') || null);
}
const getItemName = (item) => {
    // Override the foundation name
    if (item.productCode === 'foundation') {
        return 'Foundation & Bunkie Board';
    }
    return item.genericName || item.name;
};
/**
 * format data for analytics events
 * @param {object} item Cart item object
 */
function getItemData(item) {
    const variant = getVariant(item);
    return {
        item_id: item.bundledVariants ? item.productCode : item.sku,
        item_name: getItemName(item),
        item_category: capitalizeFirstLetter(item.category),
        item_category2: item.subcategory,
        item_variant: variant,
        price: item.bundledVariants ? 0.0 : item.price,
        quantity: item.quantity
    };
}
/**
 * Push cartId event to datalayer when cartId is created
 * @param {string} cartId created or existing cartId
 */
async function pushCartId(cartId) {
    try {
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push({
                event: 'cartId',
                cartId
            });
        }
    }
    catch (error) {
        (0, utils_1.buildErrorMessage)(`[Analytics cartId]:`, 'Cannot push analytics event', error, false);
    }
}
/**
 * Push view_cart event to datalayer when user navigates to cart
 * @param {array} items Cart items
 */
async function viewCart(items) {
    try {
        const cartData = items.map((cartItem) => ({
            ...getItemData(cartItem),
            quantity: cartItem.quantity
        }));
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push({
                event: 'view_cart',
                ecommerce: {
                    items: cartData
                }
            });
        }
    }
    catch (error) {
        (0, utils_1.buildErrorMessage)(`[Analytics view_cart]:`, 'Cannot push analytics event', error, false);
    }
}
/**
 * Push add_to_cart event to datalayer when product is added to cart
 * @param {object} item Cart item object
 * @param {number} quantity quantity of product
 * @param {string} dimension location of event, default cart
 */
async function pushAddToCart(items, dimension = 'cart') {
    try {
        const dataLayerItems = items.map(item => {
            return {
                ...getItemData(item),
                location_id: dimension
            };
        });
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push({
                event: 'add_to_cart',
                ecommerce: {
                    items: dataLayerItems
                }
            });
        }
    }
    catch (error) {
        (0, utils_1.buildErrorMessage)(`[Analytics add_to_cart]:`, 'Cannot push analytics event', error, false);
    }
}
/**
 * Push move_to_cart event to datalayer when product is moved from savedForLater to Cart
 * @param {object} item Cart item object
 */
function pushMoveToCartFromSaveForLater(item) {
    try {
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push({
                event: 'move_to_cart',
                ecommerce: {
                    items: [
                        {
                            ...getItemData(item),
                            quantity: item.quantity
                        }
                    ]
                }
            });
        }
    }
    catch (error) {
        (0, utils_1.buildErrorMessage)(`[Analytics move_to_cart]:`, 'Cannot push analytics event', error, false);
    }
}
/**
 * Push add_to_cart or remove_from_cart event to datalayer when a product quantity is updated
 * @param {object} item Cart item object
 * @param {boolean} isAdd
 */
async function pushItemUpdate(item, isAdd, dimension = 'cart') {
    const action = isAdd ? 'add_to_cart' : 'remove_from_cart';
    try {
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push({
                event: `${action}`,
                ecommerce: {
                    items: [
                        {
                            ...getItemData(item),
                            quantity: 1,
                            location_id: dimension
                        }
                    ]
                }
            });
        }
    }
    catch (error) {
        (0, utils_1.buildErrorMessage)(`[Analytics ${action}]:`, 'Cannot push analytics event', error, false);
    }
}
/**
 * Extend events
 * @param {string} productName product name
 * @param {'extendAdd' | 'extendRemove'} extendEvent
 * @param {string} location
 */
async function pushExtendDataHandler(item, extendEvent, location = 'cart') {
    try {
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push({
                event: `${extendEvent}`,
                ecommerce: {
                    items: [
                        {
                            ...getItemData(item),
                            quantity: 1,
                            location_id: location
                        }
                    ]
                }
            });
        }
    }
    catch (error) {
        (0, utils_1.buildErrorMessage)(`[Analytics ${extendEvent}]:`, 'Cannot push analytics event', error, false);
    }
}
/**
 * Extend events
 * @param {string} productName product name
 * @param {'extendAdd' | 'extendRemove'} extendEvent
 * @param {string} location
 */
async function pushBundleBreak(item, location = 'cart') {
    try {
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push({
                event: 'bundle_break',
                items: [
                    {
                        ...getItemData(item),
                        quantity: 1,
                        location_id: location
                    }
                ]
            });
        }
    }
    catch (error) {
        (0, utils_1.buildErrorMessage)(`[Analytics bundle break}]:`, 'Cannot push analytics event', error, false);
    }
}
/**
 * Extend events
 * @param {string} productName product name
 * @param {'extendAdd' | 'extendRemove'} extendEvent
 * @param {string} location
 */
async function pushFirstBundleBreak(item, location = 'cart') {
    try {
        if (typeof window !== 'undefined' && window.dataLayer) {
            window.dataLayer.push({
                event: 'first_bundle_break',
                items: [
                    {
                        ...getItemData(item),
                        quantity: 1,
                        location_id: location
                    }
                ]
            });
        }
    }
    catch (error) {
        (0, utils_1.buildErrorMessage)(`[Analytics first bundle break]:`, 'Cannot push analytics event', error, false);
    }
}
exports.analyticsEvents = {
    pushCartId,
    viewCart,
    pushAddToCart,
    pushMoveToCartFromSaveForLater,
    pushItemUpdate,
    pushExtendDataHandler,
    pushBundleBreak,
    pushFirstBundleBreak
};
//# sourceMappingURL=analytics.js.map